import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ApiService from '../services/api';
import { Article, Category, Period } from '../types';

const Search: React.FC = () => {
  const { category: categorySlug, period: periodSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');

  const itemsPerPage = 6;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [articlesData, categoriesData, periodsData] = await Promise.all([
          ApiService.getArticles({
            category: categorySlug,
            period: periodSlug,
            search: searchParams.get('q') || undefined,
            limit: itemsPerPage * 5, // Load more for pagination
            offset: 0
          }),
          ApiService.getCategories(),
          ApiService.getPeriods()
        ]);
        
        setArticles(articlesData);
        setCategories(categoriesData);
        setPeriods(periodsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categorySlug, periodSlug, searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setSearchParams({ q: searchTerm });
    } else {
      setSearchParams({});
    }
    setCurrentPage(1);
  };

  const getCurrentPageArticles = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return articles.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = () => Math.ceil(articles.length / itemsPerPage);

  const getPageTitle = () => {
    if (categorySlug) {
      const category = categories.find(c => c.slug === categorySlug);
      return category?.name || 'Category';
    }
    if (periodSlug) {
      const period = periods.find(p => p.slug === periodSlug);
      return period?.name || 'Period';
    }
    if (searchParams.get('q')) {
      return `Search Results for "${searchParams.get('q')}"`;
    }
    return 'All Articles';
  };

  const getPageDescription = () => {
    if (categorySlug) {
      const category = categories.find(c => c.slug === categorySlug);
      return category?.description || 'Explore articles in this category';
    }
    if (periodSlug) {
      const period = periods.find(p => p.slug === periodSlug);
      return period?.description || 'Explore articles from this historical period';
    }
    if (searchParams.get('q')) {
      return `Found ${articles.length} articles matching your search`;
    }
    return 'Discover fascinating articles about history, culture, and civilization.';
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="bg-[#fdfcf9] text-[#2c2825] min-h-screen">
      <div className="layout-content-container flex flex-col max-w-[1200px] flex-1 mx-auto px-4 py-12">
        <div className="flex flex-col items-center text-center p-4">
          <h2 className="font-newsreader text-5xl font-bold leading-tight">{getPageTitle()}</h2>
          <p className="text-[#6b6460] text-lg font-normal leading-normal max-w-2xl mt-4">
            {getPageDescription()}
          </p>
        </div>

        <div className="px-4 py-8">
          <form onSubmit={handleSearch} className="relative">
            <input 
              className="form-input w-full min-w-0 resize-none overflow-hidden rounded-full border border-solid border-[#e7e3e0] bg-[#fdfcf9] py-3 px-4 text-base text-[#2c2825] placeholder:text-[#a89d96] focus:border-[#38e07b] focus:ring-[#38e07b]"
              placeholder={categorySlug ? `Search within ${getPageTitle()}` : "Search articles"} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
        </div>

        {/* Filter tabs */}
        <div className="pb-6">
          <div className="flex border-b border-[#e7e3e0] px-4 gap-8">
            <Link 
              className={`flex flex-col items-center justify-center border-b-2 pb-3 pt-2 ${
                !categorySlug && !periodSlug ? 'border-b-[#2c2825] text-[#2c2825]' : 'border-b-transparent text-[#6b6460] hover:text-[#2c2825]'
              }`}
              to="/search"
            >
              <p className="text-base font-bold leading-normal">All</p>
            </Link>
            {categories.slice(0, 4).map((category) => (
              <Link 
                key={category.id}
                className={`flex flex-col items-center justify-center border-b-2 pb-3 pt-2 ${
                  categorySlug === category.slug ? 'border-b-[#2c2825] text-[#2c2825]' : 'border-b-transparent text-[#6b6460] hover:text-[#2c2825]'
                }`}
                to={`/category/${category.slug}`}
              >
                <p className="text-base font-medium leading-normal">{category.name}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Articles grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 p-4">
          {getCurrentPageArticles().map((article) => (
            <Link 
              key={article.id}
              to={`/article/${article.slug}`}
              className="flex flex-col gap-2 group"
            >
              <div 
                className="w-full bg-center bg-no-repeat aspect-[4/3] bg-cover rounded-lg shadow-sm overflow-hidden"
                style={{ backgroundImage: `url(${article.image_url})` }}
              />
              <div className="flex flex-col">
                <h3 className="font-newsreader text-sm font-bold leading-tight group-hover:text-[#38e07b] line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-[#6b6460] text-xs font-normal leading-normal mt-1 line-clamp-2">
                  {article.excerpt}
                </p>
                <p className="text-[#a89d96] text-xs mt-1">
                  By {article.author}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {getTotalPages() > 1 && (
          <div className="flex items-center justify-center p-4 mt-8">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex size-10 items-center justify-center rounded-full hover:bg-[#f7f5f3] disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[#6b6460]">chevron_left</span>
            </button>
            
            {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`text-base font-medium leading-normal flex size-10 items-center justify-center rounded-full mx-1 ${
                  currentPage === page 
                    ? 'text-white bg-[#38e07b]' 
                    : 'text-[#6b6460] hover:bg-[#f7f5f3]'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(Math.min(getTotalPages(), currentPage + 1))}
              disabled={currentPage === getTotalPages()}
              className="flex size-10 items-center justify-center rounded-full hover:bg-[#f7f5f3] disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[#6b6460]">chevron_right</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;